import { action, computed, makeObservable, observable } from 'mobx';
import { YaspaBankingApiPayOutRequestType } from 'src/api_openapi/v2/yaspa/schemas';
import { Common } from 'src/AppState/Common';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import {
    apiWithdrawInitiateRequest,
    ApiWithdrawInitiateResponse200Type,
} from 'src/api_openapi/v2/yaspa/apiWithdrawInitiate';

export interface YaspaUrlResponseType {
    link: string;
}

type AmountInputType = () => string;
type ErrorType = 'depositRestriction' | 'unknown';

export interface YaspaErrorType {
    type: ErrorType;
    message: string;
}

type YaspaPopupType = { isOpen: false } | { isOpen: true; src: string };

function generateRandomAlphanumeric(length: number): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
}

export class YaspaState {
    @observable public errors: YaspaErrorType[] = [];
    @observable public lastTransactionId: number | undefined = undefined;
    @observable public popup: YaspaPopupType = { isOpen: false };

    public constructor(
        private readonly customerId: number,
        private readonly common: Common,
        private readonly models: ModelsState,
        private readonly queryAudits: () => void
    ) {
        makeObservable(this);
    }

    @computed public get getResponseLinks(): {
        successRedirectUrl: string;
        failureRedirectUrl: string;
    } {
        return {
            successRedirectUrl: `/yaspa-banking-api/v1/deposit/callback/success`,
            failureRedirectUrl: `/yaspa-banking-api/v1/deposit/callback/failure`,
        };
    }

    @computed public get customerData(): Omit<YaspaBankingApiPayOutRequestType, 'amount'> | null {
        const customer = this.models.getUserModel(this.customerId).basicData.data;

        if (customer === null) {
            return null;
        }

        const { gatewayUrl } = this.common.appEnvParams;
        const randomReferenceNumber = generateRandomAlphanumeric(15);

        return {
            customerIdentifier: customer.id.toString(),
            paymentGiro: customer.currency === 'GBP' ? 'FPS' : 'SEPA',
            reference: randomReferenceNumber,
            currency: customer.currency,
            description: 'withdraw',
            supportedCountries: ['GB'],
            language: 'EN',
            successBankRedirectUrl: `${gatewayUrl}${this.getResponseLinks.successRedirectUrl}`,
            successRedirectUrl: `${gatewayUrl}${this.getResponseLinks.successRedirectUrl}`,
            failureBankRedirectUrl: `${gatewayUrl}${this.getResponseLinks.failureRedirectUrl}`,
            failureRedirectUrl: `${gatewayUrl}${this.getResponseLinks.failureRedirectUrl}`,
            channel: 'backoffice',
        };
    }

    @action public submitDepositForm = async (getInputAmount: AmountInputType): Promise<void> => {
        const amount = getInputAmount();

        if (this.customerData === null) {
            this.createErrorMessage('Unknown customer data');
            return;
        }

        try {
            this.errors = [];

            const urlResponseData = await this.getUrlData({ ...this.customerData, amount: amount });

            if (urlResponseData === null) return;

            this.handleOpen(urlResponseData);
        } catch (e) {
            this.createErrorMessage();
        }
    };

    private getUrlData = async (
        body: YaspaBankingApiPayOutRequestType
    ): Promise<ApiWithdrawInitiateResponse200Type['data']['link'] | null> => {
        const { gatewayUrl } = this.common.appEnvParams;

        const response = await apiWithdrawInitiateRequest(
            gatewayUrl,
            { requestBody: body },
            { authorization: `Bearer ${this.common.session.currentJwt}` }
        );

        if (response.status !== 200) {
            this.createErrorMessage(response.body.errors?.[0]?.message);
            return null;
        }
        return response.body.data.link;
    };

    @action public createErrorMessage = (message?: string): void => {
        const errorType: ErrorType =
            message?.includes('depositRestriction') === true ? 'depositRestriction' : 'unknown';

        switch (errorType) {
            case 'depositRestriction':
                this.errors.push({
                    type: 'depositRestriction',
                    message: `Your deposits have been restricted.`,
                });
                break;

            case 'unknown':
                this.errors.push({
                    type: 'unknown',
                    message: message ?? 'Unknown error occurred',
                });
                break;
        }
    };

    @action public handleOpen = (src: string): void => {
        this.popup = { isOpen: true, src };
    };

    @action public handleClose = (): void => {
        this.popup = { isOpen: false };
        this.queryAudits();
    };
}
